import { LocationFormDialogContents, LocationsView } from "@tellescope/calendar";
import { CalendarPageTabsHeader } from "./CalendarPageTabsHeader";
import { useCallback, useState } from "react";
import { DestructiveConfirmDialog, Dialog } from "@tellescope/ui";
import { TellescopeModelFromName, useTellescopeMutation, useTellescopeQuery } from "@tellescope/tanstack-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

export const CalendarLocationsPage = () => {
  const { t } = useTranslation("calendar");
  const [locationBeingDeleted, setLocationBeingDeleted] = useState<TellescopeModelFromName<"appointment_locations">>();
  const [search, setSearch] = useSearchParams();
  const locationIdBeingEdited = search.get("locationId") ?? undefined;
  const [isCreating, setIsCreating] = useState(false);

  const deleteLocationMutation = useTellescopeMutation({
    model: "appointment_locations",
    method: "deleteOne",
    onSuccess: () => setLocationBeingDeleted(undefined),
    onError: () => toast.error(t("Error deleting location, please try again")),
  });

  const locationQuery = useTellescopeQuery({
    model: "appointment_locations",
    method: "getOne",
    request: locationIdBeingEdited ?? "",
    enabled: !!locationIdBeingEdited,
  });

  const closeDialog = useCallback(() => {
    setSearch(
      (prev) => {
        prev.delete("locationId");
        return prev;
      },
      { replace: true }
    );
    setIsCreating(false);
  }, [setSearch]);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      <CalendarPageTabsHeader />
      <LocationsView
        getLocationOnClick={(location) => ({
          to: `.?locationId=${location.id}`,
          replace: true,
        })}
        onDelete={(location) => setLocationBeingDeleted(location)}
        onAddLocationClick={() => {
          setIsCreating(true);
        }}
      />
      <DestructiveConfirmDialog
        open={!!locationBeingDeleted}
        onOpenChange={(open) => {
          if (!open) {
            setLocationBeingDeleted(undefined);
          }
        }}
        onConfirm={() => {
          if (locationBeingDeleted?.id) deleteLocationMutation.mutateAsync(locationBeingDeleted?.id);
        }}
        onCancel={() => setLocationBeingDeleted(undefined)}
        loading={deleteLocationMutation.isPending}
        title={t("Delete location?")}
        confirmButtonTestId="location-delete-confirm"
        description={
          locationBeingDeleted?.title
            ? t('"{{locationName}}" will be permanently removed. This cannot be undone.', {
                locationName: locationBeingDeleted?.title,
              })
            : t("This location will be permanently removed. This cannot be undone.")
        }
      />
      <Dialog
        open={locationIdBeingEdited ? !!locationQuery.data : isCreating}
        onOpenChange={(open) => {
          if (!open) {
            closeDialog();
          }
        }}
      >
        <LocationFormDialogContents
          key={`location-${isCreating ? "creation" : locationIdBeingEdited ? "editing" : "closed"}-dialog`}
          location={locationQuery.data}
          onCancel={() => {
            closeDialog();
          }}
          onSave={() => {
            closeDialog();
          }}
        />
      </Dialog>
    </div>
  );
};
